/**
 * Meu plano > Comprar plano (Vitor, 15/10; no sistema antigo, quem estava sem plano via o catalogo com preco e um botao
 * direto pro checkout, e aqui so havia um link pro site). Primeiro o ultimo plano que a pessoa comprou, pra comprar de
 * novo; depois os outros planos da marca. Quem escolhe a ordem e o servico de pagamentos (com o checkout da ultima
 * compra dela, ou o mais vendido daquele plano); a tela so diz qual foi o ultimo plano, que vem do backend.
 */
import { dolarDoReal, type MeioDoCheckout, type PlanoAVenda, type PlanosAVenda } from "@mtc/contratos";
import { Aviso, BotaoLink, CabecalhoDePagina, CartaoDeOferta, EsqueletoDeCartao, Grade, Pilha, SeloDaPromocao, Vazio, formatarDolares, formatarReais, mensagemDe, realPelaCotacao } from "@mtc/ui";
import { descontoAnunciado } from "./promocao";
import { useQuery } from "@tanstack/react-query";
import { marca } from "../config";
import { useSessao } from "../sessao";
import type { Matricula } from "../tipos";
import { chamarComSessao, Instabilidade } from "./pagamento";
import { useMatriculas } from "./Planos";

/** O plano da compra mais recente, de qualquer situacao (encerrado tambem: e justamente quem mais compra de novo). */
export function ultimoPlanoComprado(matriculas: Matricula[]): string | null {
  const comData = matriculas.filter((m) => m.comprada_em);
  const ultima = comData.sort((a, b) => b.comprada_em!.localeCompare(a.comprada_em!))[0] ?? matriculas[0];
  return ultima?.plano.slug ?? null;
}

/** "PIX ou cartão em até 12x", "Só PIX", "Cartão à vista": como se paga, numa linha. */
function comoPaga(meios: MeioDoCheckout[], parcelas: number): string {
  const cartao = meios.includes("cartao") ? (parcelas > 1 ? `cartão em até ${parcelas}x` : "cartão à vista") : null;
  if (meios.includes("pix")) return cartao ? `PIX ou ${cartao}` : "Só PIX";
  return cartao ? cartao.charAt(0).toUpperCase() + cartao.slice(1) : "";
}

function Oferta({ p, recompra }: { p: PlanoAVenda; recompra?: boolean }) {
  // Em dolar (Global), como no checkout (Vitor, 15/10): o dolar anunciado em destaque e o real cobrado embaixo.
  const d = p.anunciado_em_dolar;
  // Promocao valendo pro plano (spec 067): o selo com o nome e o "de / por", com o preco promocional do servidor. Em dolar,
  // o promocional e o dolar que o servidor calculou (o anunciado exato) e o real cobrado embaixo.
  const promo = p.promocao ?? null;
  const cobrado = promo ? promo.preco_centavos : p.preco_centavos;
  const cheio = d ? formatarDolares(d.preco_usd_centavos) : formatarReais(p.preco_centavos);
  const apoio = [
    p.preco_pro_centavos != null ? `Com Profit Pro, ${formatarReais(p.preco_pro_centavos)}` : null,
    d ? realPelaCotacao(cobrado, d.cotacao) : null,
    comoPaga(p.meios, p.max_parcelas),
  ].filter(Boolean);
  return (
    <CartaoDeOferta
      nome={p.nome}
      selo={promo ? <SeloDaPromocao nome={promo.nome} tema={promo.tema} desconto={descontoAnunciado(promo, { anunciado_em_dolar: d ? { ...d, cotacao_em: null } : null })} /> : undefined}
      precoDe={promo ? cheio : undefined}
      preco={!promo ? cheio : d ? formatarDolares(promo.preco_usd_centavos ?? dolarDoReal(promo.preco_centavos, d.cotacao)) : formatarReais(promo.preco_centavos)}
      apoio={apoio}
      rotulo={recompra ? "Seu último plano" : undefined}
      destaque={recompra}
      acao={<BotaoLink href={`/checkout/${p.token}`} variante={recompra ? "primario" : "secundario"}>{recompra ? "Comprar de novo" : "Comprar"}</BotaoLink>}
    />
  );
}

export function PaginaPlanosAVenda() {
  const { api } = useSessao();
  const matriculas = useMatriculas();
  const ultimo = ultimoPlanoComprado(matriculas.data?.itens ?? []);
  const q = useQuery({
    queryKey: ["planos-a-venda", ultimo],
    queryFn: () => chamarComSessao<PlanosAVenda>(api, `/v1/me/planos-a-venda${ultimo ? `?ultimo_plano=${encodeURIComponent(ultimo)}` : ""}`),
    enabled: !matriculas.isLoading,
  });
  const site = marca().linkPlanos;
  const r = q.data;
  const dados = r?.ok ? r.dados : null;
  const nada = dados && !dados.recompra && !dados.itens.length;
  return (
    <Pilha espaco={6}>
      <CabecalhoDePagina titulo="Comprar plano" descricao="Os planos à venda agora. O pagamento é no checkout, por PIX ou cartão, e o plano novo aparece aqui na sua conta." />
      {matriculas.isLoading || q.isLoading ? (
        <Grade minimo={260}><EsqueletoDeCartao linhas={3} rotulo="Buscando os planos" /><EsqueletoDeCartao linhas={3} /></Grade>
      ) : r && !r.ok ? (
        r.fora ? <Instabilidade /> : <Aviso tipo="erro">{r.erro || mensagemDe(null, "Não conseguimos buscar os planos agora. Recarregue a página.")}</Aviso>
      ) : nada || !dados ? (
        <Vazio titulo="Nenhum plano à venda por aqui agora" acao={site ? <a className="mtc-botao mtc-botao--secundario" href={site} target="_blank" rel="noreferrer">Ver no site</a> : undefined}>
          Os planos também estão no site, com os links de compra.
        </Vazio>
      ) : (
        <>
          {dados.recompra && (
            <section aria-label="Comprar de novo" style={{ maxWidth: 440 }}>
              <Oferta p={dados.recompra} recompra />
            </section>
          )}
          {dados.itens.length > 0 && (
            <section aria-label={dados.recompra ? "Outros planos" : "Planos"}>
              <Pilha espaco={3}>
                <h2 className="mtc-rotulo" style={{ margin: 0 }}>{dados.recompra ? "Outros planos" : "Planos"}</h2>
                <Grade minimo={260}>
                  {dados.itens.map((p) => <Oferta key={p.token} p={p} />)}
                </Grade>
              </Pilha>
            </section>
          )}
        </>
      )}
    </Pilha>
  );
}
